import { useEffect, useRef, useState } from 'react';

type UseLazyMountOptions = {
  /** Distance in px before the element enters the viewport to start mounting. */
  rootMargin?: string;
  /** Once mounted, stay mounted (default true). */
  persist?: boolean;
};

/**
 * Returns true once the ref element intersects the viewport (or rootMargin).
 * Use to defer heavy child trees until the user scrolls near them.
 */
export function useLazyMount(options: UseLazyMountOptions = {}) {
  const { rootMargin = '200px', persist = true } = options;
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (visible && persist) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true);
          if (persist) observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [visible, persist, rootMargin]);

  return { ref, visible };
}
